<template>
  <main class="dashboard-screen" aria-label="物业系统运营数据大屏">
    <div ref="canvas" class="dashboard-canvas">
      <DashboardHeader
        :period="period"
        :property-name="propertyName"
        :community="communityName"
        :community-id="communityId"
        :communities="communities"
        :date-range="dateRange"
        :last-updated="lastUpdated"
        :loading="loading"
        @period-change="changePeriod"
        @date-range-change="changeDateRange"
        @community-change="changeCommunity"
        @refresh="loadDashboard"
      />
      <div class="dashboard-grid">
        <div class="dashboard-top">
          <div class="dashboard-left">
            <OverviewStats class="overview" :data="dashboardData.overview" :community-count="communityCount" />
            <RoomData class="room" :data="dashboardData.rooms" />
            <OwnerAuth class="owner" :data="dashboardData.owners" />
          </div>
          <div class="dashboard-center">
            <FeeTrend class="fee" :data="dashboardData.fees" />
            <CollectionProgress class="dashboard-collection" :data="dashboardData.fees" />
          </div>
          <div class="dashboard-right">
            <BuildingRanking class="ranking" :data="dashboardData.fees" />
            <RepairAnalysis class="repair" :data="dashboardData.repairs" />
            <RepairType class="dashboard-repair-type" :data="dashboardData.repairs" />
          </div>
        </div>
        <div class="dashboard-bottom">
          <CollectionRateTrend :data="dashboardData.fees" />
          <NoticeAlerts :data="dashboardData.alerts" />
          <WorkOrderTrend :data="dashboardData.repairs" />
        </div>
      </div>
      <div v-if="error" class="dashboard-error" role="alert">
        <i class="el-icon-warning-outline"></i>{{ error }}
        <button type="button" @click="loadDashboard">重试</button>
      </div>
    </div>
  </main>
</template>

<script>
import DashboardHeader from './components/DashboardHeader.vue'
import OverviewStats from './components/OverviewStats.vue'
import RoomData from './components/RoomData.vue'
import OwnerAuth from './components/OwnerAuth.vue'
import FeeTrend from './components/FeeTrend.vue'
import CollectionProgress from './components/CollectionProgress.vue'
import BuildingRanking from './components/BuildingRanking.vue'
import RepairAnalysis from './components/RepairAnalysis.vue'
import RepairType from './components/RepairType.vue'
import CollectionRateTrend from './components/CollectionRateTrend.vue'
import NoticeAlerts from './components/NoticeAlerts.vue'
import WorkOrderTrend from './components/WorkOrderTrend.vue'
import {
  dashboardQuery,
  getDashboardAlerts,
  getDashboardContext,
  getDashboardFees,
  getDashboardOverview,
  getDashboardOwners,
  getDashboardRepairs,
  getDashboardRooms,
  historyRange,
  rangeGranularity
} from '@/api/dashboard/dashboardApi'

const EMPTY_DATA = {
  overview: {},
  rooms: { summary: {}, breakdown: [] },
  owners: { summary: {}, authTrend: [] },
  fees: { summary: {}, trend: [], floorRanking: [], aging: [] },
  repairs: { summary: {}, breakdown: [], trend: [] },
  alerts: { items: [] }
}

export default {
  name: 'PropertyDataDashboard',
  components: { DashboardHeader, OverviewStats, RoomData, OwnerAuth, FeeTrend, CollectionProgress, BuildingRanking, RepairAnalysis, RepairType, CollectionRateTrend, NoticeAlerts, WorkOrderTrend },
  data: () => ({
    period: '本月',
    dateRange: [],
    propertyName: '当前物业',
    communityId: '',
    communities: [],
    dashboardData: JSON.parse(JSON.stringify(EMPTY_DATA)),
    snapshotId: '',
    lastUpdated: '',
    loading: false,
    error: '',
    requestVersion: 0
  }),
  computed: {
    communityCount() {
      return this.communityId ? 1 : this.communities.length
    },
    communityName() {
      const selected = this.communities.find(item => String(item.communityId) === String(this.communityId))
      return selected ? selected.communityName : '全部小区'
    }
  },
  mounted() {
    this.loadDashboard()
  },
  methods: {
    changePeriod(period) {
      this.period = period
      this.dateRange = []
      this.loadDashboard()
    },
    changeDateRange(dateRange) {
      this.period = '自定义区间'
      this.dateRange = dateRange
      this.loadDashboard()
    },
    changeCommunity(communityId) {
      this.communityId = communityId || ''
      this.loadDashboard()
    },
    async loadDashboard() {
      const requestVersion = ++this.requestVersion
      this.loading = true
      this.error = ''
      try {
        const query = dashboardQuery(this.period, this.communityId, this.dateRange)
        const customRange = this.period === '自定义区间'
        const trendQuery = customRange ? query : Object.assign(historyRange(12), this.communityId ? { communityId: this.communityId } : {})
        const trendGranularity = customRange ? rangeGranularity(this.dateRange) : 'month'
        const repairGranularity = this.period === '本年' || (customRange && trendGranularity === 'month') ? 'month' : 'day'
        const context = await getDashboardContext(query)
        const snapshotId = context.snapshotId
        const [overview, currentFees, historicalFees, rooms, owners, repairs, alerts] = await Promise.all([
          getDashboardOverview(query, snapshotId),
          getDashboardFees(query, snapshotId, 'day'),
          getDashboardFees(trendQuery, snapshotId, trendGranularity),
          getDashboardRooms(query, snapshotId),
          getDashboardOwners(query, snapshotId),
          getDashboardRepairs(query, snapshotId, repairGranularity),
          getDashboardAlerts(query, snapshotId)
        ])
        const overviewData = overview.data || {}
        const currentFeeData = currentFees.data || {}
        const historicalFeeData = historicalFees.data || {}
        if (requestVersion !== this.requestVersion) return
        this.snapshotId = snapshotId
        this.lastUpdated = overview.generatedAt || context.generatedAt || ''
        this.communities = (context.data && context.data.communities) || []
        this.propertyName = (context.data && context.data.propertyName) || '当前物业'
        this.dashboardData = {
          overview: overviewData,
          rooms: rooms.data || EMPTY_DATA.rooms,
          owners: owners.data || EMPTY_DATA.owners,
          fees: Object.assign({}, historicalFeeData, {
            summary: overviewData.fees || currentFeeData.summary || {},
            aging: currentFeeData.aging || [],
            floorRanking: currentFeeData.floorRanking || []
          }),
          repairs: repairs.data || EMPTY_DATA.repairs,
          alerts: alerts.data || EMPTY_DATA.alerts
        }
      } catch (error) {
        if (requestVersion === this.requestVersion) this.error = error.message || '大屏数据加载失败'
      } finally {
        if (requestVersion === this.requestVersion) this.loading = false
      }
    }
  }
}
</script>

<style lang="scss">
html:has(.dashboard-screen), body:has(.dashboard-screen), #app:has(.dashboard-screen) { width: 100%; height: 100%; margin: 0; overflow: hidden; }
.dashboard-screen {
  --screen-bg: #020f1e;
  --screen-panel: #05283f;
  --screen-border: rgba(12, 134, 195, .7);
  --screen-line: #18a5ef;
  --screen-text: #e2eef7;
  --screen-muted: #93a8b8;
  --screen-radius: 8px;
  width: 100%;
  height: 100vh;
  min-width: 0;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: #12618b #020d19;
  scrollbar-width: thin;
  background: #020d19;
  color: var(--screen-text);
  font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;
}
.dashboard-screen, .dashboard-screen * { box-sizing: border-box; letter-spacing: 0; }
.dashboard-screen::-webkit-scrollbar { width: 8px; height: 8px; }
.dashboard-screen::-webkit-scrollbar-track { background: #020d19; }
.dashboard-screen::-webkit-scrollbar-thumb { border: 2px solid #020d19; border-radius: 4px; background: #12618b; }
.dashboard-screen::-webkit-scrollbar-thumb:hover { background: #1889be; }
.dashboard-canvas {
  width: 100%;
  min-width: 1180px;
  height: 100vh;
  min-height: 1065px;
  padding: 0 14px 12px;
  background:
    radial-gradient(circle at 50% 42%, rgba(5, 55, 88, .24), transparent 42%),
    linear-gradient(rgba(6, 31, 52, .12) 1px, transparent 1px),
    linear-gradient(90deg, rgba(6, 31, 52, .1) 1px, transparent 1px),
    #020e1b;
  background-size: auto, 28px 28px, 28px 28px, auto;
}
.dashboard-grid {
  display: grid;
  grid-template-rows: minmax(706px, 1fr) 230px;
  gap: 8px;
  height: calc(100% - 121px);
}
.dashboard-top, .dashboard-bottom { display: grid; gap: 8px; min-width: 0; min-height: 0; }
.dashboard-top { grid-template-columns: 29fr 42fr 29fr; }
.dashboard-bottom { grid-template-columns: 34fr 25fr 41fr; }
.dashboard-left, .dashboard-center, .dashboard-right { display: grid; gap: 8px; min-width: 0; min-height: 0; }
.dashboard-left { grid-template-rows: 180px 221px minmax(230px, 1fr); }
.dashboard-center { grid-template-rows: 426px minmax(230px, 1fr); }
.dashboard-right { grid-template-rows: 230px 230px minmax(230px, 1fr); }
.overview { --dashboard-card-min-height: 180px; }
.room { --dashboard-card-min-height: 221px; }
.owner, .dashboard-collection, .ranking, .repair, .dashboard-repair-type,
.dashboard-bottom > * { --dashboard-card-min-height: 230px; }
.fee { --dashboard-card-min-height: 426px; }
.dashboard-error { position: fixed; z-index: 30; right: 22px; bottom: 18px; display: flex; align-items: center; gap: 10px; max-width: 520px; padding: 10px 14px; border: 1px solid rgba(235, 91, 79, .7); border-radius: 5px; background: rgba(55, 18, 28, .94); color: #ffd9d5; font-size: 12px; }
.dashboard-error i { color: #ff786b; font-size: 16px; }
.dashboard-error button { padding: 4px 10px; border: 1px solid rgba(255, 146, 132, .7); border-radius: 3px; background: transparent; color: #ffd9d5; cursor: pointer; }
@media (max-aspect-ratio: 4/3) {
  .dashboard-screen { overflow: auto; }
  .dashboard-canvas { width: 1440px; height: 1065px; }
}
</style>
